import type { FeldWert, KonfigurationsFeld, KonfiguratorOption } from "@repo/shared";

import { euro } from "../../lib/regeln";
import { FeldRahmen, zahlText } from "./FeldRahmen";

type AuswahlFeld = Extract<KonfigurationsFeld, { art: "auswahl" }>;
type MassFeld = Extract<KonfigurationsFeld, { art: "mass" }>;

type Knopf = { wert: FeldWert; label: string; betont: boolean; gesperrt: boolean; titel: string | undefined };

/** Tooltip einer Option: Hinweis (bei gesperrten der Grund) und Preis je Einheit. */
function titelZu(option: KonfiguratorOption): string | undefined {
  const preis = option.preis ? `${euro(option.preis.nettoCents)}${option.preis.einheit ? ` / ${option.preis.einheit}` : ""}` : null;
  return [option.hinweis, preis].filter(Boolean).join(" · ") || undefined;
}

/** Alle Stufen eines Maßes von min bis max – ohne Rundungsreste wie 0,30000000000000004. */
function stufen(feld: MassFeld): number[] {
  const anzahl = Math.floor((feld.max - feld.min) / feld.schritt + 1e-9);
  return Array.from({ length: anzahl + 1 }, (_, i) => Number((feld.min + i * feld.schritt).toFixed(10)));
}

/**
 * Knopfreihe für Auswahlen und für Maße mit wenigen Stufen. Hervorgehobene
 * Optionen sind fett, ein Preis steht im Tooltip.
 */
export function Knopfreihe({
  feld,
  wert,
  onChange,
  beschriftungVerbergen,
}: {
  feld: AuswahlFeld | MassFeld;
  wert: FeldWert | undefined;
  onChange: (wert: FeldWert) => void;
  beschriftungVerbergen?: boolean;
}) {
  const knoepfe: Knopf[] =
    feld.art === "auswahl"
      ? feld.optionen.map((option) => ({
          wert: option.wert,
          label: option.label,
          betont: option.hervorgehoben === true,
          gesperrt: option.gesperrt === true,
          titel: titelZu(option),
        }))
      : stufen(feld).map((stufe) => ({
          wert: stufe,
          // „Stk“ versteht sich bei Anzahlen von selbst.
          label: feld.einheit && feld.einheit !== "Stk" ? `${zahlText(stufe)} ${feld.einheit}` : zahlText(stufe),
          betont: false,
          gesperrt: false,
          titel: undefined,
        }));
  // Maße kommen als Zahl, aus gespeicherten Werten auch als Text.
  const gewaehlt = (knopf: Knopf) =>
    feld.art === "auswahl" || typeof wert !== "string" ? wert === knopf.wert : wert !== "" && Number(wert) === knopf.wert;

  return (
    <FeldRahmen feld={feld} beschriftungVerbergen={beschriftungVerbergen}>
      <div className="konfi-knoepfe">
        {knoepfe.map((knopf) => (
          <button
            key={String(knopf.wert)}
            type="button"
            className={knopf.betont ? "konfi-knopf konfi-knopf--betont" : "konfi-knopf"}
            aria-pressed={gewaehlt(knopf)}
            disabled={feld.zustand.gesperrt || knopf.gesperrt}
            title={knopf.titel}
            onClick={() => onChange(knopf.wert)}
          >
            {knopf.label}
          </button>
        ))}
      </div>
    </FeldRahmen>
  );
}
